import { useCallback, useEffect, useRef, useState } from 'react'
import type { StoredFontSummary } from '@/types'
import {
  deleteFont,
  loadFontSummaries,
  registerStoredFont,
  releaseRegisteredFonts,
  saveFontBinary,
  unregisterFont,
} from '@/services/fontStorage'
import { getFontFormat, isValidFontFile } from '@/utils/fileToBase64'
import { DEFAULT_FONT } from '@/constants'
import {
  MAX_STORED_FONT_BYTES,
  isActiveFontReady,
  resolveFailedActiveFontReplacement,
  resolveUnavailableActiveFont,
  startFontShell,
} from '@/v2/fonts/FontRegistry'
import { fontFamilyNameFromFilename } from '@/v2/fonts/fontFamilyName'

interface UseFontManagerOptions {
  activeFont: string
  onActiveFontChange: (name: string) => void
}

interface UseFontManagerReturn {
  fonts: StoredFontSummary[]
  activeFont: string
  isLoading: boolean
  error: string | null
  setActiveFont: (name: string) => void
  addFont: (file: File) => Promise<void>
  removeFont: (name: string) => Promise<void>
  isShellReady: boolean
  isFontReady: boolean
}

/**
 * Browser integration for binary font persistence.
 *
 * The selected font is controlled by the project/editor state. Saved-font list
 * hydration never writes that selection, so startup cannot replace a restored
 * custom font with the default font.
 */
export function useFontManager({
  activeFont,
  onActiveFontChange,
}: UseFontManagerOptions): UseFontManagerReturn {
  const [fonts, setFonts] = useState<StoredFontSummary[]>([])
  const [isShellLoading, setIsShellLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [shellReady, setShellReady] = useState(false)
  const [registeredActiveFont, setRegisteredActiveFont] = useState(DEFAULT_FONT)
  const selectionRevision = useRef(0)
  const listRevision = useRef(0)
  const lifecycleRevision = useRef(0)
  const mountedRef = useRef(false)
  const activeFontRef = useRef(activeFont)
  const previousActiveFontRef = useRef(activeFont)
  const fontMutationCounter = useRef(0)
  const fontMutationOwners = useRef(new Map<string, number>())
  if (previousActiveFontRef.current !== activeFont) {
    previousActiveFontRef.current = activeFont
    selectionRevision.current += 1
  }
  activeFontRef.current = activeFont

  const refreshSavedFonts = useCallback(async () => {
    const lifecycle = lifecycleRevision.current
    if (!mountedRef.current) return
    const revision = listRevision.current + 1
    listRevision.current = revision
    try {
      const storedFonts = await loadFontSummaries()
      if (mountedRef.current
        && lifecycleRevision.current === lifecycle
        && listRevision.current === revision) {
        setFonts(storedFonts)
      }
    } catch (reason) {
      if (!mountedRef.current
        || lifecycleRevision.current !== lifecycle
        || listRevision.current !== revision) return
      console.error('Failed to load saved font metadata:', reason)
      setError('Some saved fonts could not be listed.')
    }
  }, [])

  // The shell waits only for the bundled default. Saved metadata hydrates later.
  useEffect(() => {
    let cancelled = false
    mountedRef.current = true
    lifecycleRevision.current += 1
    setIsShellLoading(true)
    setError(null)

    void startFontShell({
      prepareDefaultFont: async () => {
        await document.fonts.ready
        await document.fonts.load(`48px "${DEFAULT_FONT}"`)
      },
      loadSavedFonts: refreshSavedFonts,
      onSavedFonts: () => undefined,
      onSavedFontsError: () => undefined,
    }).then(
      () => {
        if (cancelled) return
        setShellReady(true)
        setIsShellLoading(false)
      },
      (reason: unknown) => {
        if (cancelled) return
        console.error('Failed to initialize fonts:', reason)
        setError('Failed to load saved fonts. Using the bundled default font instead.')
        setShellReady(true)
        setIsShellLoading(false)
      },
    )

    return () => {
      cancelled = true
      mountedRef.current = false
      lifecycleRevision.current += 1
      selectionRevision.current += 1
      listRevision.current += 1
      releaseRegisteredFonts()
    }
  }, [refreshSavedFonts])

  // A restored custom font is the only saved font registered during startup.
  useEffect(() => {
    if (!shellReady) return
    if (activeFont === DEFAULT_FONT) {
      setRegisteredActiveFont(DEFAULT_FONT)
      return
    }
    setRegisteredActiveFont((current) => current === activeFont ? current : DEFAULT_FONT)
    let cancelled = false
    void registerStoredFont(activeFont).then(
      (registered) => {
        if (cancelled) return
        if (registered) {
          setRegisteredActiveFont(activeFont)
        } else {
          const recovery = resolveUnavailableActiveFont(activeFont, DEFAULT_FONT)
          setError(`The selected font “${activeFont}” is no longer available. Using the bundled default font instead.`)
          if (recovery !== null) {
            setRegisteredActiveFont(DEFAULT_FONT)
            onActiveFontChange(recovery.activeFont)
          }
        }
      },
      (reason: unknown) => {
        if (cancelled) return
        console.error(`Failed to register selected font ${activeFont}:`, reason)
        const recovery = resolveUnavailableActiveFont(activeFont, DEFAULT_FONT)
        setError(`Failed to load the selected font “${activeFont}”. Using the bundled default font instead.`)
        if (recovery !== null) {
          setRegisteredActiveFont(DEFAULT_FONT)
          onActiveFontChange(recovery.activeFont)
        }
      },
    )
    return () => {
      cancelled = true
      unregisterFont(activeFont)
    }
  }, [activeFont, onActiveFontChange, shellReady])

  const addFont = useCallback(async (file: File) => {
    if (!isValidFontFile(file)) {
      setError('Invalid font file. Please use TTF, OTF, WOFF, or WOFF2.')
      return
    }
    if (file.size === 0 || file.size > MAX_STORED_FONT_BYTES) {
      setError(`Font files must be between 1 byte and ${MAX_STORED_FONT_BYTES / 1024 / 1024} MB.`)
      return
    }
    const name = fontFamilyNameFromFilename(file.name)
    if (name === null) {
      setError('The font filename must contain at least one letter or number.')
      return
    }

    if (!mountedRef.current) return
    setError(null)
    const lifecycle = lifecycleRevision.current
    const revision = selectionRevision.current + 1
    selectionRevision.current = revision
    let savedName: string | null = null
    let operationName: string | null = null
    let mutation = 0
    let replacementMarkedUnready = false
    let phase: 'read' | 'save' | 'register' = 'read'

    try {
      const format = getFontFormat(file.name)
      operationName = name
      mutation = fontMutationCounter.current + 1
      fontMutationCounter.current = mutation
      fontMutationOwners.current.set(name, mutation)
      const dateAdded = Date.now()
      const data = await file.arrayBuffer()
      if (!mountedRef.current
        || lifecycleRevision.current !== lifecycle
        || fontMutationOwners.current.get(name) !== mutation) return
      if (selectionRevision.current === revision && activeFontRef.current === name) {
        replacementMarkedUnready = true
        setRegisteredActiveFont(DEFAULT_FONT)
      }
      phase = 'save'
      await saveFontBinary({ name, format, dateAdded, data })
      savedName = name
      if (!mountedRef.current
        || lifecycleRevision.current !== lifecycle
        || fontMutationOwners.current.get(name) !== mutation) return

      phase = 'register'
      const registered = await registerStoredFont(name)
      if (!mountedRef.current || lifecycleRevision.current !== lifecycle) {
        if (fontMutationOwners.current.get(name) === mutation) unregisterFont(name)
        return
      }
      if (!registered) throw new Error('Saved font could not be registered')
      if (fontMutationOwners.current.get(name) !== mutation) return
      if (activeFontRef.current === name) setRegisteredActiveFont(name)
      const storedFont: StoredFontSummary = { name, format, dateAdded }
      setFonts((previous) => [
        storedFont,
        ...previous.filter((font) => font.name !== name),
      ])
      if (selectionRevision.current === revision) {
        onActiveFontChange(name)
      } else if (activeFontRef.current !== name) {
        unregisterFont(name)
      }
      void refreshSavedFonts()
    } catch (reason) {
      if (savedName !== null
        && fontMutationOwners.current.get(savedName) === mutation
        && mountedRef.current
        && lifecycleRevision.current === lifecycle) {
        try {
          await deleteFont(savedName)
        } catch (cleanupError) {
          console.warn(`Could not remove failed font ${savedName}:`, cleanupError)
        }
      }
      if (!mountedRef.current || lifecycleRevision.current !== lifecycle) return
      const stillOwnsOperation = operationName !== null
        && fontMutationOwners.current.get(operationName) === mutation
      if (stillOwnsOperation
        && selectionRevision.current === revision
        && activeFontRef.current === operationName) {
        const recovery = resolveFailedActiveFontReplacement(
          activeFontRef.current,
          operationName,
          savedName !== null,
          DEFAULT_FONT,
        )
        if (recovery !== null) {
          setRegisteredActiveFont(DEFAULT_FONT)
          onActiveFontChange(recovery.activeFont)
        } else if (replacementMarkedUnready) {
          // The storage write failed, so FontRegistry deliberately retained the old face.
          setRegisteredActiveFont(operationName)
        }
      }
      console.error('Failed to add font:', reason)
      setError(phase === 'read'
        ? 'Failed to read the font file.'
        : phase === 'save'
          ? 'Failed to save the font. Browser storage may be full or unavailable.'
          : 'The browser could not load this font. It may be corrupted or use an unsupported font format.')
    } finally {
      if (operationName !== null && fontMutationOwners.current.get(operationName) === mutation) {
        fontMutationOwners.current.delete(operationName)
      }
    }
  }, [onActiveFontChange, refreshSavedFonts])

  const removeFont = useCallback(async (name: string) => {
    if (name === DEFAULT_FONT) {
      setError('Cannot remove the default font.')
      return
    }

    let mutation = 0
    try {
      if (!mountedRef.current) return
      const lifecycle = lifecycleRevision.current
      const revision = selectionRevision.current + 1
      selectionRevision.current = revision
      mutation = fontMutationCounter.current + 1
      fontMutationCounter.current = mutation
      fontMutationOwners.current.set(name, mutation)
      await deleteFont(name)
      if (!mountedRef.current
        || lifecycleRevision.current !== lifecycle
        || fontMutationOwners.current.get(name) !== mutation) return
      fontMutationOwners.current.delete(name)
      setFonts((previous) => previous.filter((font) => font.name !== name))
      if (selectionRevision.current === revision && activeFontRef.current === name) {
        onActiveFontChange(DEFAULT_FONT)
      }
      void refreshSavedFonts()
    } catch (reason) {
      if (!mountedRef.current) return
      console.error('Failed to remove font:', reason)
      setError('Failed to remove font.')
    } finally {
      if (fontMutationOwners.current.get(name) === mutation) {
        fontMutationOwners.current.delete(name)
      }
    }
  }, [onActiveFontChange, refreshSavedFonts])

  const handleSetActiveFont = useCallback((name: string) => {
    setError(null)
    const revision = selectionRevision.current + 1
    selectionRevision.current = revision
    if (name === DEFAULT_FONT) {
      onActiveFontChange(name)
      return
    }

    void registerStoredFont(name).then(
      (registered) => {
        if (!mountedRef.current || selectionRevision.current !== revision) {
          if (registered
            && activeFontRef.current !== name
            && !fontMutationOwners.current.has(name)) {
            unregisterFont(name)
          }
          return
        }
        if (registered) {
          onActiveFontChange(name)
        } else {
          setError(`The font “${name}” is no longer available.`)
        }
      },
      (reason: unknown) => {
        if (!mountedRef.current || selectionRevision.current !== revision) return
        console.error(`Failed to select font ${name}:`, reason)
        setError(`Failed to load the font “${name}”.`)
      },
    )
  }, [onActiveFontChange])

  const isFontReady = isActiveFontReady(
    shellReady,
    activeFont,
    registeredActiveFont,
    DEFAULT_FONT,
  )

  return {
    fonts,
    activeFont,
    isLoading: isShellLoading || !isFontReady,
    error,
    setActiveFont: handleSetActiveFont,
    addFont,
    removeFont,
    isShellReady: shellReady,
    isFontReady,
  }
}
